import { useCallback, useEffect, useRef } from 'react';

/**
 * Issue #26 — a stale asynchronous response must never overwrite newer panel state.
 *
 * The Dayboard's schedule panels re-read on several independent signals (the 60 s poll, `ticket-updated`, page
 * return / reconnect, realtime). Two reads can be in flight at once and answer in either order; applying whatever
 * arrives last let an OLDER answer replace a newer one (a decision already taken shown again, a new conflict hidden)
 * until the next read.
 *
 * `begin()` starts a read and returns `isLatest()`: true only while no later read has begun and the component is still
 * mounted. A read applies its result only if `isLatest()` — so of any set of overlapping reads, exactly the most
 * recently STARTED one decides what is shown, whatever order the answers arrive in. `invalidate()` makes every read in
 * flight stale (e.g. when the panel stops showing anything). Display only: nothing here writes, and no decision is ever
 * taken from a stale read — every write still goes to the server, which re-checks it.
 */
export function useLatestResponseGuard(): { begin: () => () => boolean; invalidate: () => void } {
  const seq = useRef(0);
  useEffect(() => () => { seq.current += 1; }, []);
  const begin = useCallback(() => {
    const mine = ++seq.current;
    return () => mine === seq.current;
  }, []);
  const invalidate = useCallback(() => { seq.current += 1; }, []);
  return { begin, invalidate };
}
